import React from "react";
import { FiCheck, FiArrowLeft, FiArrowRight } from "react-icons/fi";
import Notification from "../notification/Notification";
import "../saas/SaaSUIToolkit.css";
import "./formkit.css";

/**
 * Shell for multi-step forms: sticky side stepper, content card and sticky footer.
 *
 * steps:      [{ key, title, description }]
 * currentKey: key of the active step
 * reachedKeys: keys the user may jump back to (completed steps)
 * stepFormId: id of the <form> in the current step; the Next button submits it
 * onCancel:   optional; adds a Cancel button and hides Back on the first step
 * onNext:     used instead of form submit when the step has no form (review step)
 */
const WizardLayout = ({
  title,
  subtitle,
  steps,
  currentKey,
  reachedKeys = [],
  onStepClick,
  onBack,
  onExit,
  onCancel,
  onNext,
  stepFormId,
  nextLabel = "Next",
  submitting = false,
  isLast = false,
  loading = false,
  footerNote,
  children,
}) => {
  const currentIndex = steps.findIndex((s) => s.key === currentKey);

  return (
    <div className="fk-page">
      <Notification />
      <div className="fk-header">
        <h1 className="fk-title">{title}</h1>
        {subtitle && <p className="fk-subtitle">{subtitle}</p>}
      </div>

      <div className="fk-shell">
        <div className="fk-stepbar" role="status" aria-live="polite">
          <div className="fk-stepbar__row">
            <span className="fk-stepbar__count">
              Step {currentIndex + 1} of {steps.length}
            </span>
            <span className="fk-stepbar__title">{steps[currentIndex]?.title}</span>
          </div>
          <div className="fk-stepbar__track" aria-hidden="true">
            <div className="fk-stepbar__bar" style={{ width: `${((currentIndex + 1) / steps.length) * 100}%` }} />
          </div>
        </div>

        <ol className="fk-stepper" aria-label="Form progress">
          {steps.map((s, i) => {
            const isCurrent = s.key === currentKey;
            const isDone = i < currentIndex || (!isCurrent && reachedKeys.includes(s.key));
            const clickable = !loading && !submitting && !isCurrent && (isDone || i < currentIndex);
            return (
              <li
                key={s.key}
                className={`fk-step ${isCurrent ? "fk-step--current" : ""} ${isDone ? "fk-step--done" : ""}`}
              >
                <button
                  type="button"
                  className="fk-step__btn"
                  disabled={!clickable}
                  onClick={() => onStepClick && onStepClick(s.key)}
                  aria-current={isCurrent ? "step" : undefined}
                >
                  <span className="fk-step__dot" aria-hidden="true">
                    {isDone ? <FiCheck size={14} /> : i + 1}
                  </span>
                  <span className="fk-step__label">
                    <span className="fk-step__name">{s.title}</span>
                    {s.description && <span className="fk-step__desc">{s.description}</span>}
                  </span>
                </button>
              </li>
            );
          })}
        </ol>

        <main className="fk-card">
          {loading ? (
            <div aria-busy="true" aria-label="Loading form">
              <div className="fk-skel" style={{ height: 24, width: "40%", marginBottom: 20 }} />
              <div className="fk-grid">
                {[0, 1, 2, 3].map((n) => (
                  <div key={n} className="fk-skel" style={{ height: 62 }} />
                ))}
              </div>
            </div>
          ) : (
            <div key={currentKey} className="fk-step-enter">
              {children}
            </div>
          )}
        </main>
      </div>

      <div className="fk-footer">
        {onCancel ? (
          <div className="fk-footer__left">
            <button type="button" className="fk-btn fk-btn--ghost" onClick={onCancel} disabled={submitting}>
              Cancel
            </button>
            {currentIndex > 0 && (
              <button type="button" className="fk-btn fk-btn--ghost" onClick={onBack} disabled={submitting || loading}>
                <FiArrowLeft size={16} /> Back
              </button>
            )}
          </div>
        ) : (
          <button
            type="button"
            className="fk-btn fk-btn--ghost"
            onClick={currentIndex <= 0 ? onExit : onBack}
            disabled={(currentIndex <= 0 && !onExit) || submitting || loading}
          >
            <FiArrowLeft size={16} /> Back
          </button>
        )}
        <span className="fk-footer__meta">
          Step {currentIndex + 1} of {steps.length}
          {footerNote ? ` · ${footerNote}` : ""}
        </span>
        {stepFormId && !isLast ? (
          <button type="submit" form={stepFormId} className="fk-btn fk-btn--primary" disabled={submitting || loading}>
            {nextLabel} <FiArrowRight size={16} />
          </button>
        ) : (
          <button type="button" className="fk-btn fk-btn--primary" onClick={onNext} disabled={submitting || loading}>
            {submitting && <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />}
            {nextLabel}
            {!isLast && <FiArrowRight size={16} />}
          </button>
        )}
      </div>
    </div>
  );
};

export default WizardLayout;
